import * as React from 'react';
import { useContext, useEffect, useState } from 'react';
import { auth, db } from "../firebase";
import { Timestamp, doc, getDoc, serverTimestamp, setDoc, updateDoc } from "firebase/firestore";
import { User,
  updatePassword,
  updateProfile,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  sendPasswordResetEmail } from "firebase/auth";
import { AuthContextModel } from './AuthContextInterface';
import { CurrentUserInfoType, MemberType, ProviderProps, UserState } from './ContextTypes';
import { timestampToDate, userClaimableFromDB } from './Utils';

const AuthContext = React.createContext<AuthContextModel>({} as AuthContextModel);

export function useAuth() : AuthContextModel {
  return useContext(AuthContext);
}

export function AuthProvider({ children } : ProviderProps) : JSX.Element {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [currentUserInfo, setCurrentUserInfo] = useState<CurrentUserInfoType>();
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    updateCurrentUserInfo();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser]);

  async function updateCurrentUserInfo() {
    if (!currentUser) return setCurrentUserInfo(undefined);

    const userSnap = await getDoc(doc(db, "users", currentUser.uid));
    if (!userSnap.exists()) return setCurrentUserInfo(undefined);

    setCurrentUserInfo({
      uid: currentUser.uid,
      isApproved: Boolean(userSnap.get("approved")),
      isAdmin: Boolean(userSnap.get("admin")),
      isDriver: Boolean(userSnap.get("driver")),
      freeFlightID: userSnap.get("freeFlightID"),
      passengerCount: userSnap.get("passengerCount"),
      firstName: userSnap.get("firstName"),
      lastName: userSnap.get("lastName"),
      userState: userSnap.get("userState"),
      formSigned: timestampToDate(userSnap.get("formSigned")),
      dob: timestampToDate(userSnap.get("dob")),
      fcmTokens: userSnap.get("fcmTokens"),
      phoneNumber: userSnap.get("phoneNumber"),
      owes: userSnap.get("owes"),
      lastFlew: userSnap.get("lastFlew"),
      memberType: userSnap.get("memberType"),
      solo: userClaimableFromDB(userSnap.get("solo")),
    });
  }

  async function signup(email: string, password: string,  firstName: string, lastName: string, memberType:MemberType, dob: Date) {
    const user = await createUserWithEmailAndPassword(auth, email, password);
    
    await setUserDetails(
      firstName,
      lastName,
      memberType,
      dob,
      user.user 
    )
  }

  async function unapprovedMessage() {
    if (!currentUserInfo) throw new Error("Current User Info Not Set");

    let docName = "UnapprovedFullDefault";
    if (currentUserInfo.memberType === MemberType.trial) {
      docName = "UnapprovedTrialDefault";
    }

    const message = await getDoc(doc(db, "messages", docName));

    const header = message.get("header") || "You are not yet approved";
    const text = message.get("text") || "SUGC \"dot\" net requires new users to be manually approved, please bare with us.";

    const tuple : [string, string] = [header, text];
    return tuple;
  }

  function login(email: string, password: string) {
    return signInWithEmailAndPassword(auth, email, password);
  }

  function logout() {
    return auth.signOut();
  }

  function resetPassword(email: string) {
    return sendPasswordResetEmail(auth, email);
  }

  function updateUserPassword(password: string) {
    if (!currentUser) throw new Error("Not logged in");
    return updatePassword(currentUser, password);
  }

  async function setUserDetails(firstName: string, lastName: string, memberType: MemberType, dob: Date, user: User) {
    await setDoc(doc(db, `users/${user.uid}`), {
      firstName: firstName,
      lastName: lastName,
      dob: Timestamp.fromDate(dob),
      memberType: memberType,
    },
    {
      merge: true,
    })

    await updateProfile(user, {
      displayName: firstName
    });
  }
  
  async function markDetailsAsSet(user: User) {
    const payment = await getDoc(doc(db, "miscInvoices", user.uid))
    if (!payment.exists()) return await
      updateDoc(doc(
        db,
        "users",
        user.uid
      ), {
        userState: UserState.approved
      });
    else return await
      updateDoc(doc(
        db,
        "users",
        user.uid
      ), {
        userState: UserState.newOutstandingPayment
      });
  }
  
  async function markOnBoardingAsUpdated() {
    if (!currentUser) return;
    await updateDoc(doc(
      db,
      "users",
      currentUser.uid
    ), {
      formSigned: serverTimestamp(),
    });
    await updateCurrentUserInfo();
  }

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(user => {
      setCurrentUser(user);
      setLoading(false);
    })

    return unsubscribe;
  }, [])

  const value = {
    currentUser,
    login,
    signup,
    logout,
    resetPassword,
    updatePassword: updateUserPassword,
    currentUserInfo,
    updateCurrentUserInfo,
    setUserDetails,
    markDetailsAsSet,
    markOnBoardingAsUpdated,
    unapprovedMessage,
  }

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  )
}
